import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { LedgerEntry, ClientLedger, RevenuesLedger } from '../../types';
import { generateLedgerKey } from './keys';
import { getClientById } from './clients';
import { getInvoicesByClientId } from './invoices';

// Ledger Entries Collection (DEPRECATED - Use addRevenuesLedgerEntry or addExpensesLedgerEntry instead)
export const addLedgerEntry = async (entry: Omit<LedgerEntry, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'ledgerEntries'), {
      ...entry,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    throw new Error(`Error adding ledger entry: ${error}`);
  }
};

// Add entry to revenues ledger
export const addRevenuesLedgerEntry = async (clientId: string, entry: Omit<LedgerEntry, 'id' | 'createdAt' | 'clientId'>): Promise<void> => {
  try {
    // Get the existing ledger
    const ledger = await getRevenuesLedgerByClientId(clientId);
    if (!ledger) {
      throw new Error('Revenues ledger not found for client');
    }

    // Create new entry with ID
    const newEntry: LedgerEntry = {
      id: Date.now().toString(),
      clientId,
      ...entry,
      createdAt: new Date()
    };

    // Add to existing entries and sort by date in ascending order, but keep opening balance at the beginning
    const updatedEntries = [...ledger.entries, newEntry];
    updatedEntries.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
      if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
      
      // If both are opening balance, maintain original order
      if (a.type === 'adjustment' && b.type === 'adjustment' && 
          a.description === 'Opening Balance' && b.description === 'Opening Balance') {
        return 0;
      }
      
      // Otherwise sort by date
      return a.date.getTime() - b.date.getTime();
    });

    // Recalculate running balance for all entries
    let runningBalance = 0;
    const entriesWithRecalculatedBalance = updatedEntries.map(entry => {
      runningBalance += (entry.debit || 0) - (entry.credit || 0);
      return { ...entry, balance: runningBalance };
    });

    // Update the ledger
    await updateRevenuesLedger(ledger.id, entriesWithRecalculatedBalance);
  } catch (error) {
    throw new Error(`Error adding revenues ledger entry: ${error}`);
  }
};

// Update a single revenues ledger entry (e.g., remarks, credit, debit)
export const updateRevenuesLedgerEntry = async (
  clientId: string,
  entryId: string,
  updates: Partial<Pick<LedgerEntry, 'remarks' | 'description' | 'credit' | 'debit'>>
): Promise<void> => {
  const ledger = await getRevenuesLedgerByClientId(clientId);
  if (!ledger) throw new Error('Revenues ledger not found');
  const entries = ledger.entries.map((e) => (e.id === entryId ? { ...e, ...updates } : e));
  await updateRevenuesLedger(ledger.id, entries);
};

// Update payment entry with credit and description
export const updatePaymentEntry = async (
  clientId: string,
  entryId: string,
  credit: number,
  description: string
): Promise<void> => {
  const ledger = await getRevenuesLedgerByClientId(clientId);
  if (!ledger) throw new Error('Revenues ledger not found');
  
  // Update the specific entry
  const entries = ledger.entries.map((e) => (e.id === entryId ? { ...e, credit, description } : e));
  
  // Sort entries by date to ensure proper order, but keep opening balance at the beginning
  entries.sort((a, b) => {
    // If one is opening balance, it should come first
    if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
    if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
    
    // If both are opening balance, maintain original order
    if (a.type === 'adjustment' && b.type === 'adjustment' && 
        a.description === 'Opening Balance' && b.description === 'Opening Balance') {
      return 0;
    }
    
    // Otherwise sort by date
    return a.date.getTime() - b.date.getTime();
  });
  
  // Recalculate running balance for all entries
  let runningBalance = 0;
  const updatedEntries = entries.map(entry => {
    runningBalance += (entry.debit || 0) - (entry.credit || 0);
    return { ...entry, balance: runningBalance };
  });
  
  await updateRevenuesLedger(ledger.id, updatedEntries);
};

// Delete a revenues ledger entry
export const deleteRevenuesLedgerEntry = async (clientId: string, entryId: string): Promise<void> => {
  try {
    const ledger = await getRevenuesLedgerByClientId(clientId);
    if (!ledger) throw new Error('Revenues ledger not found');
    
    // Filter out the entry to delete
    const updatedEntries = ledger.entries.filter(entry => entry.id !== entryId);
    
    // Update the ledger (balance will be recalculated automatically)
    await updateRevenuesLedger(ledger.id, updatedEntries);
  } catch (error) {
    throw new Error(`Error deleting revenues ledger entry: ${error}`);
  }
};

export const getLedgerEntriesByClientId = async (clientId: string): Promise<LedgerEntry[]> => {
  try {
    const q = query(
      collection(db, 'ledgerEntries'),
      where('clientId', '==', clientId)
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as LedgerEntry[];
    
    // Sort by date in ascending order
    result.sort((a, b) => a.date.getTime() - b.date.getTime());
    
    return result;
  } catch (error) {
    throw new Error(`Error getting ledger entries: ${error}`);
  }
};

// Get client ledger with calculated balances from approved invoices
export const getClientLedger = async (clientId: string): Promise<ClientLedger> => {
  try {
    // First try to get the new ledger system
    const revenuesLedger = await getRevenuesLedgerByClientId(clientId);
    if (revenuesLedger) {
      return {
        clientId: revenuesLedger.clientId,
        clientName: revenuesLedger.clientName,
        entries: revenuesLedger.entries,
        currentBalance: revenuesLedger.currentBalance
      };
    }

    // Fallback to old system if new ledger doesn't exist
    const [client, approvedInvoices, savedEntries] = await Promise.all([
      getClientById(clientId),
      getInvoicesByClientId(clientId),
      getLedgerEntriesByClientId(clientId)
    ]);

    if (!client) {
      throw new Error('Client not found');
    }

    // Build unified entries list: opening balance, invoices (debits), saved payments (credits)
    const unified: Array<Omit<LedgerEntry, 'id'>> = [] as any;

    // Add opening balance first (it should always be at the beginning)
    const openingBalance = (client as any).openingBalance || 0;
    if (openingBalance > 0) {
      unified.push({
        clientId: client.id,
        date: new Date('1900-01-01'), // Use a very old date to ensure it always stays at top
        description: 'Opening Balance',
        debit: openingBalance,
        credit: 0,
        balance: openingBalance,
        invoiceId: undefined,
        type: 'adjustment',
        createdBy: 'system',
        createdAt: new Date(client.createdAt)
      } as any);
    }

    approvedInvoices.forEach(invoice => {
      unified.push({
        clientId: invoice.clientId,
        date: invoice.date,
        description: invoice.invoiceDescription || `Invoice #${invoice.invoiceNumber}`,
        debit: invoice.total,
        credit: 0,
        balance: 0,
        invoiceId: invoice.id,
        type: 'invoice',
        createdBy: invoice.createdBy,
        createdAt: invoice.createdAt,
        remarks: 'outstand',
        // Include full invoice details for display
        invoiceNumber: invoice.invoiceNumber,
        awbNumber: invoice.awbNumber,
        blNumber: invoice.blNumber,
        containerNumber: invoice.containerNumber,
        portOfLoading: invoice.portOfLoading,
        portOfDischarge: invoice.portOfDischarge,
        airportOfDeparture: invoice.airportOfDeparture,
        airportOfDestination: invoice.airportOfDestination,
        shippingMethod: invoice.shippingMethod
      } as any);
    });

    savedEntries.forEach(entry => {
      unified.push({
        clientId: entry.clientId,
        date: entry.date,
        description: entry.description || 'Payment',
        debit: 0,
        credit: entry.credit,
        balance: 0,
        invoiceId: entry.invoiceId,
        type: entry.type,
        createdBy: entry.createdBy,
        createdAt: entry.createdAt
      } as any);
    });

    // Sort by date ascending, but keep opening balance at the beginning
    unified.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
      if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
      
      // Sort by date, then by creation time for entries with the same date
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // If dates are the same, sort by creation time
      const createdA = new Date(a.createdAt).getTime();
      const createdB = new Date(b.createdAt).getTime();
      return createdA - createdB;
    });

    // Compute running balance
    let runningBalance = 0;
    const entriesWithBalance = unified.map((e, idx) => {
      runningBalance += (e.debit || 0) - (e.credit || 0);
      return {
        id: `${idx}`,
        ...e,
        balance: runningBalance
      } as LedgerEntry;
    });

    return {
      clientId: client.id,
      clientName: client.name,
      entries: entriesWithBalance,
      currentBalance: runningBalance
    };
  } catch (error) {
    throw new Error(`Error getting client ledger: ${error}`);
  }
};

// Revenues Ledgers Collection
export const createRevenuesLedger = async (clientId: string, clientName: string, openingBalance: number = 0): Promise<string> => {
  try {
    const ledgerId = generateLedgerKey(clientName);
    
    // Check if ledger already exists
    const existingLedger = await getRevenuesLedger(ledgerId);
    if (existingLedger) {
      // Generate new key if exists
      const newLedgerId = generateLedgerKey(clientName);
      const entries = openingBalance > 0 ? [{
        id: '0',
        clientId,
        date: new Date(),
        description: 'Opening Balance',
        debit: openingBalance,
        credit: 0,
        balance: openingBalance,
        type: 'adjustment',
        createdBy: 'system',
        createdAt: new Date()
      }] : [];
      
      const docRef = doc(db, 'revenuesLedgers', newLedgerId);
      await setDoc(docRef, {
        id: newLedgerId,
        clientId,
        clientName,
        entries,
        currentBalance: openingBalance,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return newLedgerId;
    }
    
    const entries = openingBalance > 0 ? [{
      id: '0',
      clientId,
      date: new Date(),
      description: 'Opening Balance',
      debit: openingBalance,
      credit: 0,
      balance: openingBalance,
      type: 'adjustment',
      createdBy: 'system',
      createdAt: new Date()
    }] : [];
    
    const docRef = doc(db, 'revenuesLedgers', ledgerId);
    await setDoc(docRef, {
      id: ledgerId,
      clientId,
      clientName,
      entries,
      currentBalance: openingBalance,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return ledgerId;
  } catch (error) {
    throw new Error(`Error creating revenues ledger: ${error}`);
  }
};

export const getRevenuesLedger = async (ledgerId: string): Promise<RevenuesLedger | null> => {
  try {
    const docRef = doc(db, 'revenuesLedgers', ledgerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        clientId: data.clientId,
        clientName: data.clientName,
        entries: data.entries?.map((entry: any) => ({
          ...entry,
          date: entry.date?.toDate() || new Date(entry.date),
          createdAt: entry.createdAt?.toDate() || new Date(entry.createdAt)
        })) || [],
        currentBalance: data.currentBalance || 0,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as RevenuesLedger;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting revenues ledger: ${error}`);
  }
};

export const updateRevenuesLedger = async (ledgerId: string, entries: LedgerEntry[]): Promise<void> => {
  try {
    // Recalculate running balance for all entries to ensure accuracy
    let runningBalance = 0;
    const entriesWithRecalculatedBalance = entries.map(entry => {
      runningBalance += (entry.debit || 0) - (entry.credit || 0);
      return { ...entry, balance: runningBalance };
    });
    
    // Calculate current balance (which should equal the last entry's balance)
    const currentBalance = runningBalance;
    
    // Clean entries to remove undefined values
    const cleanEntries = entriesWithRecalculatedBalance.map(entry => {
      const cleanEntry: any = {
        id: entry.id,
        clientId: entry.clientId,
        date: entry.date,
        description: entry.description,
        debit: entry.debit || 0,
        credit: entry.credit || 0,
        balance: entry.balance || 0,
        type: entry.type,
        createdBy: entry.createdBy,
        createdAt: entry.createdAt
      };
      
      // Only add optional fields if they exist and are not undefined
      if (entry.invoiceId !== undefined) cleanEntry.invoiceId = entry.invoiceId;
      if (entry.invoiceNumber !== undefined) cleanEntry.invoiceNumber = entry.invoiceNumber;
      if (entry.awbNumber !== undefined) cleanEntry.awbNumber = entry.awbNumber;
      if (entry.blNumber !== undefined) cleanEntry.blNumber = entry.blNumber;
      if (entry.containerNumber !== undefined) cleanEntry.containerNumber = entry.containerNumber;
      if (entry.packageCount !== undefined) cleanEntry.packageCount = entry.packageCount;
      if (entry.portOfLoading !== undefined) cleanEntry.portOfLoading = entry.portOfLoading;
      if (entry.portOfDischarge !== undefined) cleanEntry.portOfDischarge = entry.portOfDischarge;
      if (entry.airportOfDeparture !== undefined) cleanEntry.airportOfDeparture = entry.airportOfDeparture;
      if (entry.airportOfDestination !== undefined) cleanEntry.airportOfDestination = entry.airportOfDestination;
      if (entry.shippingMethod !== undefined) cleanEntry.shippingMethod = entry.shippingMethod;
      if (entry.remarks !== undefined) cleanEntry.remarks = entry.remarks;
      
      return cleanEntry;
    });
    
    const docRef = doc(db, 'revenuesLedgers', ledgerId);
    await updateDoc(docRef, {
      entries: cleanEntries,
      currentBalance,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new Error(`Error updating revenues ledger: ${error}`);
  }
};

export const getRevenuesLedgerByClientId = async (clientId: string): Promise<RevenuesLedger | null> => {
  try {
    const q = query(
      collection(db, 'revenuesLedgers'),
      where('clientId', '==', clientId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      clientId: data.clientId,
      clientName: data.clientName,
      entries: data.entries?.map((entry: any) => ({
        ...entry,
        date: entry.date?.toDate() || new Date(entry.date),
        createdAt: entry.createdAt?.toDate() || new Date(entry.createdAt)
      })) || [],
      currentBalance: data.currentBalance || 0,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as RevenuesLedger;
  } catch (error) {
    throw new Error(`Error getting revenues ledger by client ID: ${error}`);
  }
};

// Helper function to rebuild ledger from approved invoices
export const rebuildRevenuesLedger = async (clientId: string): Promise<void> => {
  try {
    const [client, approvedInvoices] = await Promise.all([
      getClientById(clientId),
      getInvoicesByClientId(clientId)
    ]);

    if (!client) {
      throw new Error('Client not found');
    }

    // Get or create ledger
    let ledger = await getRevenuesLedgerByClientId(clientId);
    if (!ledger) {
      const openingBalance = (client as any).openingBalance || 0;
      const ledgerId = await createRevenuesLedger(clientId, client.name, openingBalance);
      ledger = await getRevenuesLedger(ledgerId);
    }

    if (!ledger) {
      throw new Error('Failed to create or retrieve ledger');
    }

    // Preserve existing payment entries and invoice remarks from current ledger
    const existingPaymentEntries = ledger.entries.filter(entry => entry.type === 'payment');
    const existingInvoiceEntries = ledger.entries.filter(entry => entry.type === 'invoice');

    // Build unified entries list with ID preservation
    const unified: Array<{ entry: Omit<LedgerEntry, 'id'>, id?: string }> = [];

    // Add opening balance first (it should always be at the beginning)
    const openingBalance = (client as any).openingBalance || 0;
    if (openingBalance > 0) {
      unified.push({
        entry: {
          clientId: client.id,
          date: new Date('1900-01-01'), // Use a very old date to ensure it always stays at top
          description: 'Opening Balance',
          debit: openingBalance,
          credit: 0,
          balance: openingBalance,
          type: 'adjustment',
          createdBy: 'system',
          createdAt: new Date(client.createdAt)
        } as any
      });
    }

    // Add all approved invoices
    approvedInvoices.forEach(invoice => {
      // Find existing invoice entry to preserve remarks
      const existingEntry = existingInvoiceEntries.find(entry => entry.invoiceId === invoice.id);
      const remarks = existingEntry ? (existingEntry as any).remarks : 'outstand';
      
      unified.push({
        entry: {
          clientId: invoice.clientId,
          date: invoice.date,
          description: invoice.invoiceDescription || `Invoice #${invoice.invoiceNumber}`,
          debit: invoice.total,
          credit: 0,
          balance: 0,
          invoiceId: invoice.id,
          type: 'invoice',
          createdBy: invoice.createdBy,
          createdAt: invoice.createdAt,
          remarks: remarks, // Preserve remarks from existing entry
          // Include full invoice details for display
          invoiceNumber: invoice.invoiceNumber,
          awbNumber: invoice.awbNumber,
          blNumber: invoice.blNumber,
          containerNumber: invoice.containerNumber,
          portOfLoading: invoice.portOfLoading,
          portOfDischarge: invoice.portOfDischarge,
          airportOfDeparture: invoice.airportOfDeparture,
          airportOfDestination: invoice.airportOfDestination,
          shippingMethod: invoice.shippingMethod
        } as any
      });
    });

    // Add existing payment entries (preserve them)
    existingPaymentEntries.forEach(entry => {
      unified.push({
        entry: {
          clientId: entry.clientId,
          date: entry.date,
          description: entry.description || 'Payment',
          debit: entry.debit || 0,
          credit: entry.credit || 0,
          balance: 0, // Will be recalculated
          invoiceId: entry.invoiceId,
          type: entry.type,
          createdBy: entry.createdBy,
          createdAt: entry.createdAt
        } as any,
        id: entry.id // Preserve the original ID to maintain edit/delete functionality
      });
    });

    // Sort by date ascending, but keep opening balance at the beginning
    unified.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.entry.type === 'adjustment' && a.entry.description === 'Opening Balance') return -1;
      if (b.entry.type === 'adjustment' && b.entry.description === 'Opening Balance') return 1;
      
      // Sort by date, then by creation time for entries with the same date
      const dateA = new Date(a.entry.date).getTime();
      const dateB = new Date(b.entry.date).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // If dates are the same, sort by creation time
      const createdA = new Date(a.entry.createdAt).getTime();
      const createdB = new Date(b.entry.createdAt).getTime();
      return createdA - createdB;
    });

    // Compute running balance
    let runningBalance = 0;
    const entriesWithBalance = unified.map((item, idx) => {
      runningBalance += (item.entry.debit || 0) - (item.entry.credit || 0);
      return {
        id: item.id || `${idx}`, // Preserve original ID if available
        ...item.entry,
        balance: runningBalance
      } as LedgerEntry;
    });

    // Update the ledger
    await updateRevenuesLedger(ledger.id, entriesWithBalance);
  } catch (error) {
    throw new Error(`Error rebuilding revenues ledger: ${error}`);
  }
};
